import React from 'react';
import { FormattedMessage } from 'react-intl';
import { toNumber } from 'lodash';

import { FormattedDate } from 'components/FormattedDateTime/FormattedDate';
import FormattedPrice from 'components/FormattedPrice/FormattedPrice';
import { Step } from 'components/OnboardingForm/OnboardingSteps';
import { Fields, Values } from 'components/OnboardingForm/types';

import ProgressNav from './ProgressNav';
import ProgressNavItem from './ProgressNavItem';
import ProgressSubNav from './ProgressSubNav';
import ProgressSubNavItem from './ProgressSubNavItem';

interface Props {
  activeStep: Step;
  values: Values;
  isPreferredAdviserStepEnabled: boolean;
  isDisclosureStepEnabled: boolean;
}

export default function OnboardingProgressNav({
  activeStep,
  values,
  isPreferredAdviserStepEnabled,
  isDisclosureStepEnabled,
}: Props) {
  const isOurAdviceServiceSectionEnabled = isPreferredAdviserStepEnabled || isDisclosureStepEnabled;

  return activeStep > Step.BeginKnownClient ? (
    <ProgressNav activeStep={activeStep}>
      <ProgressNavItem
        text={<FormattedMessage id="onboarding.sections.legalAgreements" />}
        steps={[Step.Consents]}
      >
        <ProgressSubNav>
          <ProgressSubNavItem step={Step.Consents}>
            <FormattedMessage id={`onboarding.progress.${Step.Consents}`} />
          </ProgressSubNavItem>
        </ProgressSubNav>
      </ProgressNavItem>

      <ProgressNavItem
        text={<FormattedMessage id="onboarding.sections.personalDetails" />}
        steps={[Step.FirstName, Step.LastName, Step.Age]}
      >
        <ProgressSubNav>
          <ProgressSubNavItem step={Step.FirstName}>
            {({ isComplete }) =>
              isComplete ? (
                values[Fields.FirstName]
              ) : (
                <FormattedMessage id={`onboarding.progress.${Step.FirstName}`} />
              )
            }
          </ProgressSubNavItem>

          <ProgressSubNavItem step={Step.LastName}>
            {({ isComplete }) =>
              isComplete ? (
                values[Fields.LastName]
              ) : (
                <FormattedMessage id={`onboarding.progress.${Step.LastName}`} />
              )
            }
          </ProgressSubNavItem>

          <ProgressSubNavItem step={Step.Age}>
            {({ isComplete }) =>
              isComplete ? (
                <FormattedDate value={values[Fields.DateOfBirth]} dateStyle="long" />
              ) : (
                <FormattedMessage id={`onboarding.progress.${Step.Age}`} />
              )
            }
          </ProgressSubNavItem>
        </ProgressSubNav>
      </ProgressNavItem>

      <ProgressNavItem
        text={<FormattedMessage id="onboarding.sections.planningDetails" />}
        steps={[
          Step.OtherFinancialNeeds,
          Step.Contribution,
          Step.HighInterestDebt,
          Step.CashReserves,
        ]}
      >
        <ProgressSubNav>
          <ProgressSubNavItem step={Step.OtherFinancialNeeds}>
            <FormattedMessage id={`onboarding.progress.${Step.OtherFinancialNeeds}`} />
          </ProgressSubNavItem>

          {[Step.OtherFinancialNeeds, Step.Contribution].includes(activeStep) ? (
            <ProgressSubNavItem step={Step.Contribution}>
              <FormattedMessage id={`onboarding.progress.${Step.Contribution}`} />
            </ProgressSubNavItem>
          ) : (
            <>
              <ProgressSubNavItem step={Step.Contribution}>
                <FormattedMessage
                  id="onboarding.progress.initialInvestment"
                  values={{
                    value: (
                      <FormattedPrice value={toNumber(values[Fields.InitialInvestment]) || 0} />
                    ),
                  }}
                />
              </ProgressSubNavItem>

              <ProgressSubNavItem step={Step.Contribution}>
                <FormattedMessage
                  id="onboarding.progress.monthlyContribution"
                  values={{
                    value: (
                      <FormattedPrice value={toNumber(values[Fields.MonthlyContribution]) || 0} />
                    ),
                  }}
                />
              </ProgressSubNavItem>
            </>
          )}

          <ProgressSubNavItem step={Step.HighInterestDebt}>
            <FormattedMessage id={`onboarding.progress.${Step.HighInterestDebt}`} />
          </ProgressSubNavItem>

          <ProgressSubNavItem step={Step.CashReserves}>
            <FormattedMessage id={`onboarding.progress.${Step.CashReserves}`} />
          </ProgressSubNavItem>
        </ProgressSubNav>
      </ProgressNavItem>

      {isOurAdviceServiceSectionEnabled && (
        <ProgressNavItem
          text={<FormattedMessage id="onboarding.sections.ourAdviceService" />}
          steps={[Step.PreferredAdviser, Step.Disclosure]}
        >
          {isPreferredAdviserStepEnabled && (
            <ProgressSubNav>
              <ProgressSubNavItem step={Step.PreferredAdviser}>
                <FormattedMessage id={`onboarding.progress.${Step.PreferredAdviser}`} />
              </ProgressSubNavItem>
            </ProgressSubNav>
          )}

          {isDisclosureStepEnabled && (
            <ProgressSubNav>
              <ProgressSubNavItem step={Step.Disclosure}>
                <FormattedMessage id={`onboarding.progress.${Step.Disclosure}`} />
              </ProgressSubNavItem>
            </ProgressSubNav>
          )}
        </ProgressNavItem>
      )}

      <ProgressNavItem
        text={<FormattedMessage id="onboarding.sections.account" />}
        steps={[Step.Contact]}
      >
        <ProgressSubNav>
          <ProgressSubNavItem step={Step.Contact}>
            <FormattedMessage id={`onboarding.progress.${Step.Contact}`} />
          </ProgressSubNavItem>
        </ProgressSubNav>
      </ProgressNavItem>
    </ProgressNav>
  ) : null;
}
